import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRevalidateOnFocus } from './useRevalidateOnFocus';

export interface UseApiResource<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

export function useApiResource<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: ReadonlyArray<unknown>
): UseApiResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const dataRef = useRef<T | null>(null);
  dataRef.current = data;
  const depsKey = useMemo(() => Symbol(), deps); // eslint-disable-line react-hooks/exhaustive-deps
  const previousDepsKey = useRef(depsKey);

  const reload = useCallback(() => setVersion((v) => v + 1), []);

  useRevalidateOnFocus(reload);

  useEffect(() => {
    const ctrl = new AbortController();
    let alive = true;
    const soft = previousDepsKey.current === depsKey && dataRef.current !== null;
    previousDepsKey.current = depsKey;
    if (!soft) {
      setData(null);
      setIsLoading(true);
    }
    setError(null);
    loader(ctrl.signal)
      .then((result) => {
        if (!alive) return;
        setData(result);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Falha ao carregar dados');
        if (!soft) setData(null);
        setIsLoading(false);
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey, version]);

  return { data, isLoading, error, reload };
}
